import { useCallback, useMemo, useRef, useEffect } from 'react';
import throttle from 'lodash/throttle';
import { graphql, useSubscription } from 'react-relay';
import type { GraphQLSubscriptionConfig, RecordSourceSelectorProxy } from 'relay-runtime';
import { fireErrorAnalytics } from '@jira/platform__error-reporting/src/fireErrorAnalytics.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import {
	fireTrackAnalytics,
	useAnalyticsEvents,
	fireOperationalAnalytics,
	fireUIAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { isAri, getAriConfig } from '@atlassian/jira-platform-ari/src/index.tsx';
import {
	PACKAGE_NAME,
	TEAM_NAME,
	MAX_ALLOWED_QUEUE_SIZE,
	QUERY_ISSUE_DETAILS_DELAY_TIME,
} from '../common/constants.tsx';
import type { IssueRealtimeUpdates_issueDeletedSubscription as IssueDeletedSubscription } from './__generated__/IssueRealtimeUpdates_issueDeletedSubscription.graphql';
import type { IssueRealtimeUpdates_issueUpdatedSubscription as IssueUpdatedSubscription } from './__generated__/IssueRealtimeUpdates_issueUpdatedSubscription.graphql';
import type { IssueRealtimeUpdates_issueCreatedSubscription as IssueCreatedSubscription } from './__generated__/IssueRealtimeUpdates_issueCreatedSubscription.graphql';
import { useRealtimeUpdateExceededMaxFlag } from './utils.tsx';
import type { IssueUpdateList } from './types.tsx';

const REAL_TIME_EVENT_TYPE_KEY = 'realTimeEventType';
const CREATE_ERROR_ID = 'realTimeIssueCreate';
const UPDATE_ERROR_ID = 'realTimeIssueUpdate';
const DELETE_ERROR_ID = 'realTimeIssueDelete';

type Props = {
	projectIds: string[];
	onIssuesCreatedUpdated: (issues: IssueUpdateList) => void;
	onDelete: (store: RecordSourceSelectorProxy, issueAri: string) => boolean;
	onRefetch?: () => void;
};

const issueCreatedSubscription = graphql`
	subscription IssueRealtimeUpdates_issueCreatedSubscription(
		$cloudId: ID!
		$projectIds: [String!]
	) {
		jira {
			onIssueCreatedByProjectsNoEnrichment(cloudId: $cloudId, projectIds: $projectIds) {
				resource
			}
		}
	}
`;

const issueUpdatedSubscription = graphql`
	subscription IssueRealtimeUpdates_issueUpdatedSubscription(
		$cloudId: ID!
		$projectIds: [String!]
	) {
		jira {
			onIssueUpdatedByProjectsNoEnrichment(cloudId: $cloudId, projectIds: $projectIds) {
				resource
			}
		}
	}
`;

const issueDeletedSubscription = graphql`
	subscription IssueRealtimeUpdates_issueDeletedSubscription(
		$cloudId: ID!
		$projectIds: [String!]
	) {
		jira {
			onIssueDeletedByProjectsNoEnrichment(cloudId: $cloudId, projectIds: $projectIds) {
				resource
			}
		}
	}
`;

export const IssueRealtimeUpdates = ({
	projectIds,
	onIssuesCreatedUpdated,
	onDelete,
	onRefetch,
}: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const throttledFireTrackAnalytics = useMemo(() => throttle(fireTrackAnalytics, 1000), []);
	const hasExceededMaxQueueSizeRef = useRef(false);
	const showRealtimeUpdateExceededMaxFlag = useRealtimeUpdateExceededMaxFlag();
	const cloudId = useCloudId();

	const variables = useMemo(
		() => ({
			cloudId,
			projectIds,
		}),
		[cloudId, projectIds],
	);

	const queueRef = useRef<Map<string, { issueAri: string; issueId: string }>>(new Map());
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	const processQueue = useCallback(() => {
		if (queueRef.current.size === 0 || hasExceededMaxQueueSizeRef.current) {
			return;
		}

		fireOperationalAnalytics(createAnalyticsEvent({}), 'realTimeEventQueueProcessing started', {
			queueSize: queueRef.current.size,
		});

		if (queueRef.current.size > MAX_ALLOWED_QUEUE_SIZE) {
			fireOperationalAnalytics(createAnalyticsEvent({}), 'realTimeEventsExceededMaxFlag shown', {
				queueSize: queueRef.current.size,
			});

			hasExceededMaxQueueSizeRef.current = true;
			showRealtimeUpdateExceededMaxFlag({
				onRefresh: () => {
					fireUIAnalytics(createAnalyticsEvent({}), 'realTimeEventsExceededMaxRefresh clicked');
					hasExceededMaxQueueSizeRef.current = false;
					queueRef.current = new Map();
					onRefetch?.();
				},
			});
		} else {
			onIssuesCreatedUpdated(Array.from(queueRef.current.values()));
		}

		queueRef.current = new Map();
	}, [createAnalyticsEvent, showRealtimeUpdateExceededMaxFlag, onRefetch, onIssuesCreatedUpdated]);

	const triggerDelayedProcessingOfQueue = useCallback(() => {
		// Existing timeout exists, so we don't need to create a new one
		if (timeoutRef.current) {
			return;
		}

		timeoutRef.current = setTimeout(() => {
			processQueue();
			timeoutRef.current = null;
		}, QUERY_ISSUE_DETAILS_DELAY_TIME);
	}, [processQueue]);

	useEffect(() => {
		return () => {
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	const handleCreateOrUpdateEvent = useCallback(
		(issueAri: string, issueId: string) => {
			queueRef.current.set(issueAri, { issueAri, issueId });

			triggerDelayedProcessingOfQueue();
		},
		[triggerDelayedProcessingOfQueue],
	);
	const handleCreateOrUpdateEventRef = useRef(handleCreateOrUpdateEvent);
	if (handleCreateOrUpdateEventRef.current !== handleCreateOrUpdateEvent) {
		handleCreateOrUpdateEventRef.current = handleCreateOrUpdateEvent;
	}

	const createConfig = useMemo<GraphQLSubscriptionConfig<IssueCreatedSubscription>>(
		() => ({
			variables,
			subscription: issueCreatedSubscription,
			onNext: async (response) => {
				try {
					const resourceId = response?.jira?.onIssueCreatedByProjectsNoEnrichment?.resource;
					if (resourceId && isAri(resourceId)) {
						handleCreateOrUpdateEventRef.current(resourceId, getAriConfig(resourceId).resourceId);
					}
				} catch (error) {
					fireErrorAnalytics({
						meta: {
							id: CREATE_ERROR_ID,
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						attributes: { logLocation: 'onNext' },
						error: error instanceof Error ? error : undefined,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			},
			onError: (error) => {
				fireErrorAnalytics({
					meta: {
						id: CREATE_ERROR_ID,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					attributes: { logLocation: 'onError' },
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		}),
		[variables],
	);

	const updateConfig = useMemo<GraphQLSubscriptionConfig<IssueUpdatedSubscription>>(
		() => ({
			variables,
			subscription: issueUpdatedSubscription,
			onNext: async (response) => {
				try {
					const resourceId = response?.jira?.onIssueUpdatedByProjectsNoEnrichment?.resource;
					if (resourceId && isAri(resourceId)) {
						handleCreateOrUpdateEventRef.current(resourceId, getAriConfig(resourceId).resourceId);
					}
				} catch (error) {
					fireErrorAnalytics({
						meta: {
							id: UPDATE_ERROR_ID,
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						attributes: { logLocation: 'onNext' },
						error: error instanceof Error ? error : undefined,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			},
			onError: (error) => {
				fireErrorAnalytics({
					meta: {
						id: UPDATE_ERROR_ID,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					attributes: { logLocation: 'onError' },
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		}),
		[variables],
	);

	const onDeleteRef = useRef(onDelete);
	if (onDeleteRef.current !== onDelete) {
		onDeleteRef.current = onDelete;
	}

	const deleteConfig = useMemo<GraphQLSubscriptionConfig<IssueDeletedSubscription>>(() => {
		return {
			variables,
			subscription: issueDeletedSubscription,
			updater: (store, response) => {
				try {
					if (response?.jira?.onIssueDeletedByProjectsNoEnrichment?.resource) {
						const wasDeleted = onDeleteRef.current(
							store,
							response.jira.onIssueDeletedByProjectsNoEnrichment.resource,
						);

						wasDeleted &&
							throttledFireTrackAnalytics(createAnalyticsEvent({}), 'realTimeEvent processed', {
								[REAL_TIME_EVENT_TYPE_KEY]: 'delete',
							});
					}
				} catch (error) {
					fireErrorAnalytics({
						meta: {
							id: DELETE_ERROR_ID,
							packageName: PACKAGE_NAME,
							teamName: TEAM_NAME,
						},
						attributes: { logLocation: 'updater' },
						error: error instanceof Error ? error : undefined,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
			},
			onError: (error) => {
				fireErrorAnalytics({
					meta: {
						id: DELETE_ERROR_ID,
						packageName: PACKAGE_NAME,
						teamName: TEAM_NAME,
					},
					attributes: { logLocation: 'onError' },
					error,
					sendToPrivacyUnsafeSplunk: true,
				});
			},
		};
	}, [variables, createAnalyticsEvent, throttledFireTrackAnalytics]);

	useSubscription(createConfig);
	useSubscription(updateConfig);
	useSubscription(deleteConfig);

	return null;
};
